import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';

interface BaseButtonProps {
  variant?: 'primary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  className?: string;
}

interface ButtonAsButtonProps extends BaseButtonProps, Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseButtonProps> {
  as?: 'button';
}

interface ButtonAsLinkProps extends BaseButtonProps, Omit<React.ComponentProps<typeof Link>, keyof BaseButtonProps> {
  as: typeof Link;
}

type ButtonProps = ButtonAsButtonProps | ButtonAsLinkProps;

const Button: React.FC<ButtonProps> = ({ 
  variant = 'primary', 
  size = 'md', 
  className, 
  children, 
  as: Component = 'button',
  ...props 
}) => {
  const baseClasses = "font-semibold rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-400/50 disabled:opacity-50 disabled:cursor-not-allowed [-webkit-tap-highlight-color:transparent] [-webkit-appearance:none] [appearance:none] isolate";

  const variants = {
    primary: "bg-navy-600 border-2 border-navy-700 text-white hover:bg-navy-700 hover:border-navy-800 active:scale-95 font-bold shadow-lg hover:shadow-xl",
    ghost: "bg-white border border-slate-200 text-navy-900 hover:bg-slate-50 hover:border-slate-300 active:scale-95 font-semibold shadow-sm"
  };
  
  const sizes = {
    sm: "px-4 py-2 text-sm",
    md: "px-6 py-3 text-base", 
    lg: "px-8 py-4 text-lg"
  };

  // Add inner glow shadow for glass effect (only for primary buttons)
  const glassShadow = variant === 'primary' 
    ? "0 0 0 0.1em hsla(223, 60%, 60%, 0.3) inset, 0 4px 12px -2px rgba(79, 70, 229, 0.15)"
    : "none";

  // Ensure type attribute is preserved for button elements
  const buttonProps = Component === 'button'
    ? { ...(props as ButtonAsButtonProps), type: (props as ButtonAsButtonProps).type || 'button' }
    : props;

  return (
    <Component
      className={cn(baseClasses, variants[variant], sizes[size], className)}
      style={{
        boxShadow: glassShadow,
        WebkitTapHighlightColor: 'transparent',
        outline: 'none',
        ...((buttonProps as any).style || {})
      }}
      {...buttonProps}
    >
      {children}
    </Component>
  );
};

export default Button;